import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Shield, Search, User, Crown, Briefcase, Truck, Users2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminRoles() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users/roles`, { credentials: "include" });
      if (response.ok) setUsers(await response.json());
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      if (!response.ok) throw new Error("Failed to update role");
      const updated = await response.json();
      setUsers((prev) =>
        prev.map((u) => (u.user_id === userId ? { ...u, role: updated.role } : u))
      );
      toast.success(`Role updated to ${newRole}`);
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const filteredUsers = users.filter((u) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  const getRoleIcon = (role) => {
    if (role === "admin") return <Crown size={14} className="text-amber-500" />;
    if (role === "manager") return <Briefcase size={14} className="text-blue-500" />;
    if (role === "chef") return <Crown size={14} className="text-orange-500" />;
    if (role === "rider") return <Truck size={14} className="text-purple-500" />;
    if (role === "segment_admin") return <Users2 size={14} className="text-emerald-500" />;
    return <User size={14} className="text-gray-400" />;
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-amber-100 text-amber-700",
      manager: "bg-blue-100 text-blue-700",
      chef: "bg-orange-100 text-orange-700",
      rider: "bg-purple-100 text-purple-700",
      segment_admin: "bg-emerald-100 text-emerald-700",
      user: "bg-gray-100 text-gray-600",
    };
    return styles[role] || styles.user;
  };

  const counts = {
    admin: users.filter((u) => u.role === "admin").length,
    manager: users.filter((u) => u.role === "manager").length,
    chef: users.filter((u) => u.role === "chef").length,
    rider: users.filter((u) => u.role === "rider").length,
    segment_admin: users.filter((u) => u.role === "segment_admin").length,
    user: users.filter((u) => u.role === "user").length,
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-roles">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Role Management</h1>
            <p className="text-sm text-gray-500 mt-1">Promote users to Manager or Admin roles</p>
          </div>
        </div>

        {/* Role Summary */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <Crown size={20} className="text-amber-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-700">{counts.admin}</p>
            <p className="text-xs text-amber-600">Admins</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
            <Users2 size={20} className="text-emerald-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-emerald-700">{counts.segment_admin}</p>
            <p className="text-xs text-emerald-600">Seg. Admins</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
            <Briefcase size={20} className="text-blue-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-blue-700">{counts.manager}</p>
            <p className="text-xs text-blue-600">Managers</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
            <Crown size={20} className="text-orange-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-orange-700">{counts.chef}</p>
            <p className="text-xs text-orange-600">Chefs</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
            <Truck size={20} className="text-purple-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-purple-700">{counts.rider}</p>
            <p className="text-xs text-purple-600">Riders</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
            <User size={20} className="text-gray-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-700">{counts.user}</p>
            <p className="text-[10px] text-gray-400">Users</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 bg-white"
            data-testid="roles-search"
          />
        </div>

        {/* Role Permissions Info */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase size={16} className="text-blue-600" />
              <span className="font-semibold text-sm text-blue-800">Manager Permissions</span>
            </div>
            <ul className="text-xs text-blue-700 space-y-1 ml-6 list-disc">
              <li>View and update order statuses</li>
              <li>Toggle menu item availability</li>
              <li>View dashboard stats</li>
            </ul>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Crown size={16} className="text-orange-600" />
              <span className="font-semibold text-sm text-orange-800">Chef Permissions</span>
            </div>
            <ul className="text-xs text-orange-700 space-y-1 ml-6 list-disc">
              <li>View orders assigned to their kitchen</li>
              <li>Update order status (In Process, Ready)</li>
              <li>Cancel orders & request refund</li>
            </ul>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Truck size={16} className="text-purple-600" />
              <span className="font-semibold text-sm text-purple-800">Rider Permissions</span>
            </div>
            <ul className="text-xs text-purple-700 space-y-1 ml-6 list-disc">
              <li>View only assigned deliveries</li>
              <li>Mark orders as delivered (with code)</li>
              <li>Edit own profile</li>
            </ul>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left p-4 text-gray-500 font-medium text-sm">User</th>
                  <th className="text-left p-4 text-gray-500 font-medium text-sm">Current Role</th>
                  <th className="text-left p-4 text-gray-500 font-medium text-sm">Joined</th>
                  <th className="text-right p-4 text-gray-500 font-medium text-sm">Change Role</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredUsers.map((u) => (
                  <tr key={u.user_id} className="hover:bg-gray-50" data-testid={`role-row-${u.user_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center text-sm font-bold text-green-700">
                          {(u.name || "U").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{u.name || "Unnamed"}</p>
                          <p className="text-[10px] text-gray-400">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadge(u.role)}`}>
                        {getRoleIcon(u.role)}
                        {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-500">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-"}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end">
                        <Select
                          value={u.role}
                          onValueChange={(val) => updateRole(u.user_id, val)}
                        >
                          <SelectTrigger className="w-36" data-testid={`role-select-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="segment_admin">Segment Admin</SelectItem>
                            <SelectItem value="chef">Chef</SelectItem>
                            <SelectItem value="cook">Cook</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="rider">Rider</SelectItem>
                            <SelectItem value="nutritionist">Nutritionist</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-sm">No users found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
